import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Col, Form, Image, Input, Modal, Row, Select, Upload } from "antd";
import { default as StyledSelect } from "components/Atoms/select";
import { CategorySelect } from "components/Molecules/select/category-select";
import { SubCategorySelect } from "components/Molecules/select/subcategory-select";
import { ProductGroupSelect } from "components/Molecules/select/product-group-select";
import { ExportMarketSelect } from "components/Molecules/select/export-market-select";
import {
  StaticDataContext,
  useStaticSupplierTags,
} from "context/static-data-context";
import Tag from "components/Atoms/tag";
import styled from "@emotion/styled";
import { useSubCategories } from "components/Molecules/select/api";
import { useDebounce } from "utils";
import { TOOLTIP } from "constants/tooltips";
import { InfoCircleOutlined } from "@ant-design/icons";

const Container = styled(Col)`
  height: 100%;
`;
const TooltipContent = styled.ul`
  list-style: disc;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
`;
export function EditProductModal(props: any) {
  const { isUpdating, updateProduct, handleCloseModal } = props;
  const [categoryId, setCategoryId] = useState<any>();
  const [subCategoryId, setSubCategoryId] = useState<any>();
  const {
    ageGrade: ageGradeOptions,
    serviceRequired: ServiceRequiredOptions,
    productCategories,
  } = React.useContext(StaticDataContext);
  const subCategoryRef = React.useRef();
  const [form] = Form.useForm();
  const supplierTagsOptions = useStaticSupplierTags(true);

  const handleFormValuesChange = (changedValues: any) => {
    const formFieldName = Object.keys(changedValues)[0];
    if (formFieldName === "categoryId") {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        ...changedValues,
        subcategoryId: "",
        productGroupId: "",
      });
      setCategoryId(changedValues[formFieldName]); // perform setState here
    }

    if (formFieldName === "subcategoryId") {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        ...changedValues,
        productGroupId: "",
      });
      setSubCategoryId(changedValues[formFieldName]); // perform setState here
    }
  };

  const updateCategorySubCategory = () => {
    const { categoryId, subcategoryId } = props.initialValues;
    setCategoryId(categoryId);
    setSubCategoryId(subcategoryId);
  };

  const isDisabled = React.useCallback(
    (fieldName) => {
      const { enabledFields } = props;
      if (enabledFields && enabledFields.length) {
        return enabledFields.indexOf(fieldName) < 0;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.enabledFields]
  );

  React.useEffect(() => {
    updateCategorySubCategory();
    form.setFieldsValue({ ...props.initialValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValues]);

  return (
    <Modal
      title={`Edit Product Details`}
      okText={`Save`}
      confirmLoading={isUpdating}
      destroyOnClose
      onOk={async () => {
        try {
          const values = await form.validateFields();
          updateProduct({ ...values });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(new Error("Form has not valid"));
        }
      }}
      visible={props.open}
      onCancel={() => {
        handleCloseModal();
      }}
      forceRender
    >
      <Form
        name="edit-modal"
        layout="vertical"
        form={form}
        initialValues={{ ...props.initialValues }}
        onValuesChange={handleFormValuesChange}
      >
        <div>
          <Row justify={"space-between"} gutter={[8, 8]}>
            <Col span={12}>
              <Form.Item
                name={"productName"}
                label={"Product Name"}
                rules={[
                  {
                    required: true,
                    message: "Product Name cannot be empty",
                  },
                ]}
              >
                <Input
                  disabled={isDisabled("productName")}
                  placeholder={"Enter Product Name"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"productNumber"}
                label={"Product Number"}
                rules={[
                  {
                    required: true,
                    message: "Product Number cannot be empty",
                  },
                ]}
              >
                <Input
                  disabled={isDisabled("productNumber")}
                  placeholder={"Enter Product Number"}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify={"space-between"} gutter={[8, 8]}>
            <Col span={12}>
              <Form.Item
                name={"categoryId"}
                label={"Product Category"}
                rules={[
                  {
                    required: true,
                    message: "Product Category cannot be empty",
                  },
                ]}
              >
                <CategorySelect
                  disabled={isDisabled("categoryId")}
                  placeholder={"Enter Product Category"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"subcategoryId"}
                label={"Product Sub-category"}
                rules={[
                  {
                    required: true,
                    message: "Product Sub-category cannot be empty",
                  },
                ]}
              >
                <SubCategorySelect
                  disabled={isDisabled("subcategoryId")}
                  categoryId={categoryId}
                  placeholder={"Enter Product Sub-category"}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify={"space-between"} gutter={[8, 8]}>
            <Col span={12}>
              <Form.Item
                name={"productGroupId"}
                label={"Product Group (if any)"}
              >
                <ProductGroupSelect
                  disabled={isDisabled("productGroupId")}
                  subCategoryId={subCategoryId}
                  placeholder={"Enter Product Group"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"exportMarketId"}
                label={"Export Market"}
                rules={[
                  {
                    required: true,
                    message: "Export Market cannot be empty",
                  },
                ]}
              >
                <ExportMarketSelect
                  disabled={isDisabled("exportMarketId")}
                  placeholder={"Enter Export Market"}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify={"space-between"} gutter={[8, 8]}>
            <Col span={24}>
              <Form.Item
                name={"supplierTags"}
                label={"Supplier Tag"}
                shouldUpdate={true}
                valuePropName={"value"}
              >
                <StyledSelect
                  creatableProps={{
                    // value: form.getFieldValue("supplierTags"),
                    onChange: (value: any) => {
                      form.setFields([
                        {
                          name: "supplierTags",
                          errors: [],
                          value: [...value],
                        },
                      ]);
                    },
                    isMulti: true,
                    isDisabled: isDisabled("supplierTags"),
                    components: {
                      MultiValue: (valueProps: any) => {
                        const { data, removeProps } = valueProps;
                        return (
                          <Tag closable onClose={removeProps.onClick}>
                            {data?.label}
                          </Tag>
                        );
                      },
                    },
                  }}
                  isCreatable
                  options={(supplierTagsOptions || []) as any}
                ></StyledSelect>
              </Form.Item>
            </Col>
          </Row>

          <Row justify={"space-between"} gutter={[8, 8]}>
            <Col span={12}>
              <Form.Item
                name={"ageGrade"}
                label={"Age Grade"}
                rules={[
                  {
                    required: true,
                    message: "Estimate Completion Date cannot be empty",
                  },
                ]}
              >
                <Select
                  disabled={isDisabled("ageGrade")}
                  options={ageGradeOptions}
                  placeholder="Select age grade"
                ></Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                tooltip={{
                  title: (
                    <TooltipContent>
                      {TOOLTIP.serviceRequired.map((value, index) => {
                        return (
                          <li key={`service-required-${index}`}>{value}</li>
                        );
                      })}
                    </TooltipContent>
                  ),
                  icon: <InfoCircleOutlined />,
                }}
                name={"serviceRequired"}
                label={"Service Required"}
                rules={[
                  {
                    required: true,
                    message: "Service Required cannot be empty",
                  },
                ]}
              >
                <Select
                  disabled
                  options={ServiceRequiredOptions}
                  placeholder="Select service required"
                ></Select>
              </Form.Item>
            </Col>
          </Row>
          <Row justify={"space-between"} gutter={[8, 8]}>
            <Col span={24}>
              <Form.Item
                name={"productDescription"}
                label={"Product Description"}
                rules={
                  isDisabled("productDescription")
                    ? []
                    : [
                        {
                          required: true,
                          message: "Product description can not be empty",
                        },
                      ]
                }
              >
                <Input.TextArea
                  disabled={isDisabled("productDescription")}
                  autoSize={{ minRows: 4 }}
                  placeholder="Enter product description"
                ></Input.TextArea>
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
}
